import { isUrl } from '@Utils/helpers/stringHelpers';
import { CircularProgress } from '@mui/material';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';
import Zoomist from 'zoomist';
import { QualityControlApi } from '@Data/qualityControl/Api';
import {
    centrePoleLoadedTagImage,
    centrePoleState,
    centrePoleTagImages,
} from '@Data/qualityControl/Reducer';
import QcLayoutStyles from '@Views/QualityControl/InProgressView.scss';
import Symbology from '@Views/QualityControl/styles/symbology.scss';
import { ImageCarousel } from '../SvgViewer/components/ImageCarousel';
import { CAROUSEL_HEIGHT, RESIZE_REFRESH_DELAY } from '../SvgViewer/constants';
import Styles from './EntityImagesCycle.scss';

export const EntityImagesCycle = () => {
    const { width, height, ref } = useResizeDetector({
        refreshMode: 'debounce',
        refreshRate: RESIZE_REFRESH_DELAY,
    });
    const imageRef = useRef(null);
    const zoomistRef = useRef(null);
    const centrePole = useSelector(centrePoleState);
    const imagesArray = useSelector(centrePoleTagImages);
    const loadedImage = useSelector(centrePoleLoadedTagImage);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    const setupZoomist = (imgRef: any) => {
        if (!zoomistRef.current) {
            const zoomistContainer = imgRef.current;
            if (
                !_isNil(imagesArray[currentImageIndex]) &&
                !_isNil(imagesArray[currentImageIndex].url) &&
                zoomistContainer
            ) {
                const zoomistDom = new Zoomist(zoomistContainer, {
                    bounds: true,
                    fill: 'contain',
                    height: '100%',
                    on: {
                        ready() {
                            zoomistRef.current = zoomistDom;
                        },
                    },
                });
            }
        } else if (
            zoomistRef.current &&
            zoomistRef.current.image.src !== imagesArray[currentImageIndex].url
        ) {
            const zoomist: Zoomist = zoomistRef.current;
            if (zoomist.image.src !== imagesArray[currentImageIndex].url) {
                zoomist.image.src = imagesArray[currentImageIndex].url;
            }
        }
    };

    useEffect(() => {
        setIsLoading(true);

        if (
            !_isNil(imagesArray[currentImageIndex]) ||
            !_isEmpty(centrePole.poletagImageUrls)
        ) {
            setIsLoading(false);
        }
    }, [imagesArray, centrePole]);

    useEffect(() => {
        setCurrentImageIndex(0);
    }, [loadedImage]);

    useEffect(() => {
        if (
            !_isNil(imagesArray[currentImageIndex]) &&
            !_isNil(imageRef.current)
        ) {
            setupZoomist(imageRef);
        }
    }, [imageRef, centrePole, height, currentImageIndex]);

    const handleChangeImage = async (imageIndex: number, isNext: boolean) => {
        if (imagesArray.length === 1) {
            return;
        }

        let newIndex = isNext ? currentImageIndex + 1 : currentImageIndex - 1;

        if (newIndex === -1) {
            newIndex = imagesArray.length - 1;
        }

        if (newIndex === imagesArray.length) {
            newIndex = 0;
        }

        if (!isUrl(imagesArray[newIndex]?.url)) {
            setIsLoading(true);
            await QualityControlApi.updateSingleImageUrlInPoletagArray(
                newIndex,
                imagesArray[newIndex].image_id
            );

            setIsLoading(false);
        }

        setCurrentImageIndex(newIndex);
    };

    return (
        <div ref={ref} className={QcLayoutStyles.entityimagescycleContainer}>
            <div
                ref={imageRef}
                className={Styles.imageContainer}
                data-zoomist-src={imagesArray[currentImageIndex]?.url}
                style={
                    isLoading ? { display: 'none' } : { position: 'relative' }
                }
            ></div>
            {isLoading ? (
                <CircularProgress />
            ) : (
                <React.Fragment>
                    {isLoading === false && imagesArray.length === 0 ? (
                        <div className={Styles.noImageText}>
                            No images available
                        </div>
                    ) : (
                        <ImageCarousel
                            width={QcLayoutStyles.entityImagesCycleWidth}
                            height={CAROUSEL_HEIGHT}
                            offset={0}
                            index={100}
                            title={
                                !_isEmpty(
                                    imagesArray[currentImageIndex]
                                        ?.collection_field_name
                                )
                                    ? imagesArray[currentImageIndex]
                                          .collection_field_name
                                    : 'Pole Tag'
                            }
                            onChangeImage={handleChangeImage}
                            borderColor={Symbology.centerPole}
                            style={{ position: 'relative' }}
                        />
                    )}
                </React.Fragment>
            )}
        </div>
    );
};
